import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import appConfig from "../../config/appConfig";

const TenantBrandingContext = createContext(null);

const DEFAULT_BRANDING = {
  tenant_name: null,
  tenant_plan: null,
  is_main: false,
  company_name: "VelSoft CRM",
  primary_color: "#2575fc",
  login_button_color: "#2575fc",
  logo_url: null,
  favicon_url: null,
  allow_public_employee_registration: true,
};

function applyCssVars(branding) {
  const bg = branding?.primary_color || DEFAULT_BRANDING.primary_color;
  const btn =
    branding?.login_button_color || DEFAULT_BRANDING.login_button_color;

  document.documentElement.style.setProperty("--tenant-login-bg", bg);
  document.documentElement.style.setProperty("--tenant-login-btn", btn);

  if (document.body) {
    document.body.style.setProperty("--tenant-login-bg", bg);
    document.body.style.setProperty("--tenant-login-btn", btn);
  }
}

function setFavicon(url) {
  const fallback = "/favicon.ico";
  const href = url || fallback;

  const ensureLink = (rel) => {
    let link = document.querySelector(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", rel);
      document.head.appendChild(link);
    }
    link.setAttribute("href", href);
    return link;
  };

  ensureLink("icon");
  ensureLink("shortcut icon");
}

function normalizeBranding(data) {
  return {
    tenant_name: data?.tenant_name || null,
    tenant_plan: data?.tenant_plan || null,
    is_main: data?.is_main === true,
    company_name: data?.company_name || DEFAULT_BRANDING.company_name,
    primary_color: data?.primary_color || DEFAULT_BRANDING.primary_color,
    login_button_color:
      data?.login_button_color || DEFAULT_BRANDING.login_button_color,
    logo_url: data?.logo_url || null,
    favicon_url: data?.favicon_url || null,
    allow_public_employee_registration:
      typeof data?.allow_public_employee_registration === "boolean"
        ? data.allow_public_employee_registration
        : DEFAULT_BRANDING.allow_public_employee_registration,
  };
}

export function useTenantBranding() {
  const ctx = useContext(TenantBrandingContext);

  return (
    ctx || {
      branding: DEFAULT_BRANDING,
      isLoaded: false,
      refresh: async () => {},
    }
  );
}

export default function TenantBrandingProvider({ children }) {
  const [branding, setBranding] = useState(() => {
    try {
      const cached = localStorage.getItem("tenant-branding");
      if (cached) {
        const parsed = JSON.parse(cached);
        return { ...DEFAULT_BRANDING, ...parsed };
      }
    } catch (e) {
      console.warn("[TenantBranding] Failed to read localStorage cache:", e);
    }
    return DEFAULT_BRANDING;
  });

  const [isLoaded, setIsLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${appConfig.API_BASE_URL}/public/crmBranding`, {
        method: "GET",
        credentials: "include",
      });

      if (!res.ok) {
        console.warn("[TenantBranding] API returned non-OK response:", res.status);
        setIsLoaded(true);
        return;
      }

      const data = await res.json();
      const normalized = normalizeBranding(data);

      setBranding(normalized);

      try {
        localStorage.setItem("tenant-branding", JSON.stringify(normalized));
      } catch (e) {
        console.warn("[TenantBranding] Failed to update localStorage:", e);
      }
    } catch (e) {
      console.error("[TenantBranding] Error refreshing branding:", e);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    applyCssVars(branding);
    setFavicon(branding?.favicon_url || null);
  }, [branding]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ branding, isLoaded, refresh }),
    [branding, isLoaded, refresh]
  );

  return (
    <TenantBrandingContext.Provider value={value}>
      {children}
    </TenantBrandingContext.Provider>
  );
}